"use client";

import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { PRIMARY } from "../../lib/buttonStyles";

/* ---------- helper reutilizable ---------- */
function Field({ id, label, value, onChange, placeholder, type = "text", className = "", required = false, disabled = false }) {
  return (
    <div className={`space-y-3 ${className}`}>
      <Label htmlFor={id} className="text-gray-300 font-medium text-sm">{label}</Label>
      <Input
        id={id} type={type} value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="bg-gray-800 border border-gray-700 text-white placeholder-gray-400 rounded-xl h-12
                   focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all duration-300"
      />
    </div>
  );
}

export function DatabaseConnectionDialog({ open, onOpenChange, connection, onSave, saving = false }) {
  const [formData, setFormData] = useState({
    name: "", type: "postgres",
    host: "", port: 5432,
    database: "", user: "", password: "",
    connection_uri: "",
  });

  /* -------- sincroniza props → state -------- */
  useEffect(() => {
    if (connection) {
      setFormData({
        name: connection.name ?? "",
        type: connection.engine ?? "postgres",
        host: connection.host ?? "",
        port: connection.port ?? 5432,
        database: connection.database ?? "",
        user: connection.user ?? "",
        password: "",
        connection_uri: connection.connection_uri ?? "",
      });
    } else {
      setFormData({
        name: "", type: "postgres",
        host: "", port: 5432, database: "",
        user: "", password: "", connection_uri: "",
      });
    }
  }, [connection, open]);

  /* -------- cambia tipo → puertos + campos -------- */
  const handleTypeChange = (type) => {
    const defaultPorts = { postgres: 5432, mysql: 3306, sqlserver: 1433, mongodb: 27017, redis: 6379 };
    const isURI = ["mongodb", "redis"].includes(type);
    setFormData(f => ({
      ...f,
      type,
      port: defaultPorts[type] ?? f.port,
      /* limpia campos que no aplican */
      host:          isURI ? "" : f.host,
      database:      isURI ? "" : f.database,
      user:          isURI ? "" : f.user,
      password:      isURI ? "" : f.password,
      connection_uri:isURI ? f.connection_uri : "",
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!saving) {
      onSave(formData);
    }
  };

  const isURI = ["mongodb", "redis"].includes(formData.type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] bg-black border border-gray-800 rounded-2xl shadow-2xl backdrop-blur-3xl">
        <DialogHeader className="space-y-4 pb-6">
          <DialogTitle className="text-2xl font-bold text-purple-400">
            {connection ? "Edit Database Connection" : "Add Database Connection"}
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-base">
            Configure your database connection settings
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ─── fila 1 ─── */}
          <div className="grid grid-cols-2 gap-6">
            <Field id="name" label="Connection Name" value={formData.name}
              onChange={(v) => setFormData({ ...formData, name: v })}
              placeholder="Production DB" required disabled={saving} />
            <div className="space-y-3">
              <Label className="text-gray-300 font-medium text-sm">Database Type</Label>
              <Select value={formData.type} onValueChange={handleTypeChange} disabled={saving}>
                <SelectTrigger className="bg-gray-800 border border-gray-700 text-white rounded-xl h-12 focus:border-purple-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border border-gray-700 rounded-xl">
                  <SelectItem value="postgres">PostgreSQL</SelectItem>
                  <SelectItem value="mysql">MySQL / MariaDB</SelectItem>
                  <SelectItem value="sqlserver">SQL Server</SelectItem>
                  <SelectItem value="mongodb">MongoDB (URI)</SelectItem>
                  <SelectItem value="redis">Redis (URI)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ─── campos dinámicos ─── */}
          {isURI ? (
            <Field id="connection_uri" label="Connection URI" value={formData.connection_uri}
              onChange={(v) => setFormData({ ...formData, connection_uri: v })}
              placeholder="mongodb://user:pass@host:27017/db" required disabled={saving} />
          ) : (
            <>
              <div className="grid grid-cols-3 gap-6">
                <Field id="host" label="Host" className="col-span-2"
                  value={formData.host} onChange={(v) => setFormData({ ...formData, host: v })}
                  placeholder="db.example.com" required disabled={saving} />
                <Field id="port" label="Port" type="number"
                  value={formData.port} onChange={(v) => setFormData({ ...formData, port: Number(v) })}
                  required disabled={saving} />
              </div>

              <Field id="database" label="Database Name" value={formData.database}
                onChange={(v) => setFormData({ ...formData, database: v })}
                placeholder="my_database" required disabled={saving} />

              <div className="grid grid-cols-2 gap-6">
                <Field id="user" label="Username" value={formData.user}
                  onChange={(v) => setFormData({ ...formData, user: v })}
                  placeholder="db_user" disabled={saving} />
                <Field id="password" label="Password" type="password"
                  value={formData.password} onChange={(v) => setFormData({ ...formData, password: v })}
                  placeholder="••••••••" disabled={saving} />
              </div>
            </>
          )}

          {/* ─── footer ─── */}
          <DialogFooter className="pt-6 space-x-4">
            <Button type="button" variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white rounded-xl px-6 py-3">
              Cancel
            </Button>
            <Button type="submit"
              disabled={saving}
              className={`disabled:cursor-not-allowed disabled:opacity-50 ${PRIMARY}`}>
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Saving...
                </span>
              ) : (
                <>{connection ? "Update" : "Save"} Connection</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
